import React from 'react';
import { Column } from 'react-table';
import SessionTable, { SessionTableInstanceProps } from '../index';
import { CellFunction, RESPONSE_NA } from '../../cells';
import { IEpxSurgery } from '../../../../../libs/apps/epxsurgery';

const TRI_LATERAL = {
  [-1]: 'Worse',
  [0]: 'Same',
  [1]: 'Better'
};

const DecolCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasSupplies: string = null;
  let usedSupplies: string = null;
  if (session._decol.hasSupplies == null) {
    hasSupplies = '?';
  } else if (session._decol.hasSupplies) {
    hasSupplies = 'Yes';
  } else {
    hasSupplies = 'No';
  }
  if (session._decol.hasSupplies || !session._decol.isSupplyCheck) {
    if (session._decol.usedSupplies === null) {
      usedSupplies = '?';
    } else if (session._decol.usedSupplies) {
      usedSupplies = 'Yes';
    } else {
      usedSupplies = 'No';
    }
  }
  return (
    <div>
      <b>Decolonization</b>
      <br />
      {session._decol.isSupplyCheck ? (
        <span>Has Supply: {hasSupplies !== null ? hasSupplies : null}</span>
      ) : null}
      {session._decol.usedSupplies ? (
        <span>Used Supply: {usedSupplies !== null ? usedSupplies : null}</span>
      ) : null}
    </div>
  );
};

const PainCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  const level = session._pain.level != null ? String(session._pain.level) : '?';
  const trend =
    session._pain.trend != null ? TRI_LATERAL[session._pain.trend] : null;
  return (
    <div>
      <span>
        <b>Pain:</b> {level} {trend !== null ? '(' + trend + ')' : null}
      </span>
      <br />
    </div>
  );
};

const WoundCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let degeneration = null;
  if (session._wound.degeneration === null) {
    degeneration = '?';
  } else {
    degeneration = session._wound.degeneration ? 'Yes' : 'No';
  }
  return (
    <div>
      <b>Wound Degeneration:</b> {degeneration !== null ? degeneration : null}
      <br />
    </div>
  );
};

const FeverCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let fever = null;
  let temperature = null;
  if (session._fever.fever === null) {
    fever = '?';
  } else {
    fever = session._fever.fever ? 'Yes' : 'No';
    if (session._fever.temperature) {
      temperature = session._fever.temperature;
    }
  }
  return (
    <div>
      <b>Fever:</b> {fever}{' '}
      {temperature !== null ? '(' + temperature + ')' : null} <br />
    </div>
  );
};

const PTCell: CellFunction<IEpxSurgery> = ({ row: { original: session } }) => {
  const isReceivedCheck = session._pt.isRecievedCheck;
  let hasExercises = null;
  if (session._pt.hasExercises !== null) {
    hasExercises = session._pt.hasExercises ? 'Yes' : 'No';
  } else {
    hasExercises = '?';
  }
  let didExercises = null;
  if (session._pt.didExercises !== null) {
    didExercises = session._pt.didExercises ? 'Yes' : 'No';
  } else {
    didExercises = '?';
  }
  let missedReason = null;
  if (session._pt.missedReason !== null) {
    switch (session._pt.missedReason) {
      case 1:
        missedReason = 'Pain';
        break;
      case 2:
        missedReason = 'No Time';
        break;
      case 3:
        missedReason = 'Forgot';
        break;
      case 4:
        missedReason = 'Other';
        break;
      default:
        break;
    }
  }
  return (
    <div>
      <b>PT:</b>
      <br />
      {isReceivedCheck ? (
        <span>
          <b>Received Exercises:</b> {hasExercises}
          <br />
        </span>
      ) : (
        <span>
          <b>Did Exercises:</b> {didExercises}
          <br />
        </span>
      )}
      {!didExercises && missedReason !== null ? (
        <span>
          <b>Reason:</b> {missedReason}
          <br />
        </span>
      ) : (
        <span>
          <b>Reason:</b> ?<br />
        </span>
      )}
    </div>
  );
};

const WalkingCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let didWalk = null;
  if (session._walking.didWalk === null) {
    didWalk = '?';
  } else {
    didWalk = session._walking.didWalk ? 'Yes' : 'No';
  }
  return (
    <div>
      <b>Walking</b>
      <br />
      <b>Did Walk:</b> {didWalk}
    </div>
  );
};

const MedicationCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasMedication = null;
  if (session._medication.hasMedication === null) {
    hasMedication = '?';
  } else {
    hasMedication = session._medication.hasMedication ? 'Yes' : 'No';
  }
  return (
    <div>
      <b>Medications</b> <br />
      <b>Received:</b> {hasMedication} <br />
    </div>
  );
};

const SpirometerCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasSpirometer = null;
  let usedSpirometer = null;
  if (session._spirometer.isSupplyCheck) {
    if (session._spirometer.hasSpirometer !== null) {
      hasSpirometer = session._spirometer.hasSpirometer ? 'Yes' : 'No';
    } else {
      hasSpirometer = '?';
    }
  } else {
    if (session._spirometer.usedSpirometer !== null) {
      usedSpirometer = session._spirometer.usedSpirometer ? 'Yes' : 'No';
    } else {
      usedSpirometer = '?';
    }
  }
  return (
    <div>
      <b>Spirometer</b>: <br />
      {session._spirometer.isSupplyCheck ? (
        <span>
          <b>Received:</b> {hasSpirometer}
        </span>
      ) : (
        <span>
          <b>Used:</b> {usedSpirometer}
        </span>
      )}
    </div>
  );
};

const AppointmentCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasScheduled = null;
  if (session._appointments.hasScheduled !== null) {
    hasScheduled = session._appointments.hasScheduled ? 'Yes' : 'No';
  } else {
    hasScheduled = '?';
  }
  return (
    <div>
      <b>Appointments</b> <br />
      <b>Scheduled:</b> {hasScheduled}
      <br />
    </div>
  );
};

const BreathCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasSymptoms = null;
  if (session._breath.hasSymptoms !== null) {
    hasSymptoms = session._breath.hasSymptoms ? 'Yes' : 'No';
  } else {
    hasSymptoms = '?';
  }
  return (
    <div>
      <b>Breathing</b> <br />
      <b>Has Issues:</b> {hasSymptoms}
      <br />
    </div>
  );
};

const PedsOSatCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let saturation = null;
  if (session._pedsOsat.saturation !== null) {
    saturation = session._pedsOsat.saturation ? 'Yes' : 'No';
  } else {
    saturation = '?';
  }
  return (
    <div>
      <b>O2 SAT:</b> {saturation}
      <br />
    </div>
  );
};

const PedsWeightCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let weight = null;
  if (session._pedsWeight.weight !== null) {
    weight = session._pedsWeight.weight ? 'Yes' : 'No';
  } else {
    weight = '?';
  }
  return (
    <div>
      <b>Weight:</b> {weight} grams
      <br />
    </div>
  );
};

const PedsHrCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let heartRate = null;
  if (session._pedsHr.heartRate !== null) {
    heartRate = session._pedsHr.heartRate ? 'Yes' : 'No';
  } else {
    heartRate = '?';
  }
  return (
    <div>
      <b>Heart Rate:</b> {heartRate}
      <br />
    </div>
  );
};

const PedsSvhCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let symptomsList = null;
  if (session._pedsSvh.symptomsList !== null) {
    symptomsList = session._pedsSvh.symptomsList.join(', ');
  } else {
    symptomsList = '?';
  }
  return (
    <div>
      <b>SVH:</b> <br />
      {symptomsList} <br />
    </div>
  );
};

const SOBCardiacCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let hasSymptoms = null;
  if (session._sobCardiac.hasSymptoms !== null) {
    hasSymptoms = session._sobCardiac.hasSymptoms ? 'Yes' : 'No';
  } else {
    hasSymptoms = '?';
  }
  return (
    <div>
      <b>Breathing</b> <br />
      <b>Has Issues:</b> {hasSymptoms}
      <br />
    </div>
  );
};

const DrainageCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let drainage = null;
  if (session._drainage.hasDrainage !== null) {
    drainage = session._drainage.hasDrainage ? 'Yes' : 'No';
  } else {
    drainage = '?';
  }
  return (
    <div>
      <b>Drainage:</b> {drainage}
      <br />
    </div>
  );
};

const WeightGainCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let gainedWeight = null;
  if (session._weightGain.gainedWeight !== null) {
    gainedWeight = session._weightGain.gainedWeight ? 'Yes' : 'No';
  } else {
    gainedWeight = '?';
  }
  return (
    <div>
      <b>Gained Weight:</b> {gainedWeight}
      <br />
    </div>
  );
};

const MedAdherenceCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let isTakingMeds = null;
  if (session._medAdherence.isTakingMeds !== null) {
    isTakingMeds = session._medAdherence.isTakingMeds ? 'Yes' : 'No';
  } else {
    isTakingMeds = '?';
  }
  return (
    <div>
      <span>Taking Meds:</span> {isTakingMeds}
      <br />
    </div>
  );
};

const HomeNurseCell: CellFunction<IEpxSurgery> = ({
  row: { original: session }
}) => {
  let nurseHasContacted = null;
  if (session._homeNurse.nurseHasContacted !== null) {
    nurseHasContacted = session._homeNurse.nurseHasContacted ? 'Yes' : 'No';
  } else {
    nurseHasContacted = '?';
  }
  return (
    <div>
      <span>Home Nurse Appt:</span> {nurseHasContacted}
      <br />
    </div>
  );
};

const SurgerySessionOutputs = {
  _decol: DecolCell,
  _pain: PainCell,
  _wound: WoundCell,
  _fever: FeverCell,
  _pt: PTCell,
  _walking: WalkingCell,
  _medication: MedicationCell,
  _spirometer: SpirometerCell,
  _appointments: AppointmentCell,
  _breath: BreathCell,
  _pedsOSat: PedsOSatCell,
  _pedsWeight: PedsWeightCell,
  _pedsHr: PedsHrCell,
  _pedsSvh: PedsSvhCell,
  _sobCardiac: SOBCardiacCell,
  _drainage: DrainageCell,
  _weightGain: WeightGainCell,
  _medAdherence: MedAdherenceCell,
  _homeNurse: HomeNurseCell
};

const EpxSurgeryTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxSurgery>[] = [
    {
      Header: 'Module A',
      id: '_moduleA',
      Cell: ({ row: { original: session } }) => {
        const part = session.parts[0];
        if (part) {
          const partKey = '_' + part.key;
          const child = session[partKey];
          if (child && SurgerySessionOutputs[partKey]) {
            return SurgerySessionOutputs[partKey]({
              row: { original: session }
            });
          } else {
            return RESPONSE_NA;
          }
        } else {
          return RESPONSE_NA;
        }
      }
    },
    {
      Header: 'Module B',
      id: '_moduleB',
      Cell: ({ row: { original: session } }) => {
        const part = session.parts[1];
        if (part) {
          const partKey = '_' + part.key;
          const child = session[partKey];
          if (child && SurgerySessionOutputs[partKey]) {
            return SurgerySessionOutputs[partKey]({
              row: { original: session }
            });
          } else {
            return RESPONSE_NA;
          }
        } else {
          return RESPONSE_NA;
        }
      }
    },
    {
      Header: 'Module C',
      id: '_moduleC',
      Cell: ({ row: { original: session } }) => {
        const part = session.parts[2];
        if (part) {
          const partKey = '_' + part.key;
          const child = session[partKey];
          if (child && SurgerySessionOutputs[partKey]) {
            return SurgerySessionOutputs[partKey]({
              row: { original: session }
            });
          } else {
            return RESPONSE_NA;
          }
        } else {
          return RESPONSE_NA;
        }
      }
    },
    {
      Header: 'Module D',
      id: '_moduleD',
      Cell: ({ row: { original: session } }) => {
        const part = session.parts[3];
        if (part) {
          const partKey = '_' + part.key;
          const child = session[partKey];
          if (child && SurgerySessionOutputs[partKey]) {
            return SurgerySessionOutputs[partKey]({
              row: { original: session }
            });
          } else {
            return RESPONSE_NA;
          }
        } else {
          return RESPONSE_NA;
        }
      }
    },
    {
      Header: 'Module E',
      id: '_moduleE',
      Cell: ({ row: { original: session } }) => {
        const part = session.parts[4];
        if (part) {
          const partKey = '_' + part.key;
          const child = session[partKey];
          if (child && SurgerySessionOutputs[partKey]) {
            return SurgerySessionOutputs[partKey]({
              row: { original: session }
            });
          } else {
            return RESPONSE_NA;
          }
        } else {
          return RESPONSE_NA;
        }
      }
    }
  ];
  return (
    <SessionTable<IEpxSurgery>
      url="/epxsurgery/data/"
      columns={columns}
      {...props}
    />
  );
};

export default EpxSurgeryTable;
